import * as React from 'react'
import { classnames } from '@halo/_utils/classnames'
import DataTableView from '@halo/components/data-table-view/data-table-view'
import { DataTableContext } from '@halo/components/data-table-view/context'
import { Dropdown, Form, FormInstance, Input, Menu, Modal, Radio, Table } from 'antd'
import {
    CheckCircleFilled,
    EyeOutlined,
    FolderOutlined,
    Loading3QuartersOutlined,
    QuestionCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { AppService } from '@halo/services/app.service'
import { FlagModel } from '@halo/models/flag.model'
import { Button } from '@pushly/aqe/lib/components'
import EntityDisplay, { EntityTitle } from '@halo/components/data-table-view/entity-display'
import './style/flags-list.scss'
import { StatusBadge } from '@halo/components/badges/status-badge'
import { StatusName } from '@pushly/aquarium-utils/lib/enums'
import { IApiCallOptions } from '@pushly/aqe/lib/interfaces'
import { FlagsEditor } from '@halo/portals/post-auth/flags/flag-data-form'
import randomstring from 'randomstring'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { useLoadableDataState, useService } from '@pushly/aqe/lib/hooks/index'
import { TablePaginationConfig } from 'antd/lib/table/interface'
import titleCase from 'title-case'
import { simpleNotification } from '@halo/_utils/notifications'
import deepEqual from 'react-fast-compare'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'

interface IFlagsListProps {}

interface IFlagEditorState {
    drawerKey?: string
    showFlagEditor: boolean
    flagToEdit: FlagModel | Partial<FlagModel>
}

interface IFlagsDataState {
    flags: FlagModel[]
    meta: any
}

export const FlagsList = (props: IFlagsListProps) => {
    const appSvc = useService(AppService)
    const formRef = React.createRef<FormInstance>()

    // ---------------------------------- Stateful Variables ---------------------------------- //
    const [flags, setFlags] = useLoadableDataState<IFlagsDataState>({ data: { flags: [], meta: {} } })
    const [pagination, setPagination] = React.useState<TablePaginationConfig>({
        current: 1,
        itemRender: (current: any, type: String, originalElement: any) => {
            switch (type) {
                case 'prev':
                    return <a>Previous</a>
                case 'next':
                    return <a>Next</a>
                case 'page':
                    return ''
            }

            return originalElement
        },
        onChange: async (page: number) => {
            await setPagination((config) => ({ ...config, current: page }))
        },
        pageSize: 15,
        showSizeChanger: false,
        showTotal: (total: number, range: number[]) => `Showing ${range[0]}-${range[1]} of ${total} Flags`,
        size: 'small',
        total: 15,
    })
    const [query, setQuery] = React.useState<string | undefined>()
    const [entityFilter, setEntityFilter] = React.useState<any[]>([])
    const [flagsEditor, setFlagsEditor] = React.useState<IFlagEditorState>({
        drawerKey: undefined,
        showFlagEditor: false,
        flagToEdit: new FlagModel(),
    })

    const getFlags = async () => {
        setFlags((state) => ({ ...state, loading: true }))

        const opts: IApiCallOptions = {
            query: {
                limit: pagination.pageSize,
                page: pagination.current,
                search: query,
                entities: entityFilter?.join(','),
            },
        }

        const response = await appSvc.fetchFlags(opts)

        setFlags({ loading: false, data: { flags: response.data, meta: response.meta } })
    }

    React.useEffect(() => {
        if (query?.length > 2 || query === undefined) {
            getFlags()
        }
    }, [pagination.current, query, entityFilter])

    const flagsDataSource = flags.data.flags

    const searchFlags = async (value: string): Promise<void> => {
        let qString = value.trim()

        if (qString.length < 2) {
            qString = undefined
        }

        setQuery(qString)
    }

    const getFilters = async () => {
        try {
            const flagFilters: any = {
                className: 'entity-filter',
                dataIndex: 'entity-filter',
                multiple: true,
                maxTagCount: 1,
                placeholder: 'All Entities',
                optionPrefix: 'Entities',
                onClose: (entity: any) => filterFlags(entity),
            }

            const filterOpts = await appSvc.getFlagFilters()

            if (filterOpts.length) {
                flagFilters.options = filterOpts.map((entity: string) => ({
                    group: 'Entities',
                    label: titleCase(entity),
                    value: entity,
                }))
            }

            return [flagFilters]
        } catch (error) {
            simpleNotification('error', error)
            return []
        }
    }

    const filterFlags = async (flagFilter: string[]) => {
        if (!deepEqual(flagFilter, entityFilter)) {
            flagFilter = flagFilter || []
            setEntityFilter(flagFilter)
        }
    }

    // ---------------------------------- Action Functions ---------------------------------- //
    const renderActions = (flag: FlagModel) => {
        const actions: any[] = []

        actions.push({
            key: 'archive',
            text: 'Archive',
            icon: <FolderOutlined />,
        })

        if (flag.getIsActive()) {
            actions.push({
                key: 'disable',
                text: 'Disable',
                icon: <EyeOutlined />,
            })
        } else {
            actions.push({
                key: 'enable',
                text: 'Enable',
                icon: <EyeOutlined />,
            })
        }

        return (
            <Dropdown.Button
                size="small"
                className="feeds-row-actions-btn"
                onClick={(ev) => handlePrimaryClick(flag, ev)}
                overlay={
                    actions.length === 0 ? undefined : (
                        <Menu onClick={({ key, domEvent }) => handleRowAction(key, flag, domEvent)}>
                            {actions.map((item) => (
                                <Menu.Item key={`${item.key}`}>
                                    {item.icon}
                                    <span>{item.text}</span>
                                </Menu.Item>
                            ))}
                        </Menu>
                    )
                }
            >
                <span>Edit</span>
            </Dropdown.Button>
        )
    }

    const renderFlagDetails = (flag: FlagModel): React.ReactNode => {
        return (
            <EntityDisplay
                top={{
                    left: <EntityTitle>{flag.getKey()}</EntityTitle>,
                    right: (
                        <StatusBadge
                            type="generic"
                            status={flag.getIsActive() ? StatusName.ACTIVE : StatusName.DISABLED}
                            expanded={true}
                        />
                    ),
                }}
                bottom={{
                    left: <span>{flag.getDescription()}</span>,
                }}
            />
        )
    }

    const handleArchiveFlag = async (flag: FlagModel) => {
        await appSvc.archiveFlag(flag.getKey())
        return getFlags()
    }

    const handlePrimaryClick = (flag: FlagModel, _ev: React.MouseEvent<HTMLElement>) => {
        handleOpenFlagDrawer(flag)
    }

    const renderIsActive = (flag: FlagModel, classname: string) => {
        return flag.getIsDevOnly() ? (
            <CheckCircleFilled className={classname} />
        ) : (
            <Radio style={{ pointerEvents: 'none' }} checked={false} />
        )
    }

    const handleFlagActiveStatus = async (flag: FlagModel, updateToStatus: boolean) => {
        flag.setIsActive(updateToStatus)
        await appSvc.updateFlag(flag, flag.getKey())
        return getFlags()
    }

    const handleRowAction = (
        key: string | number,
        flag: FlagModel,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void => {
        if (key === 'archive') {
            Modal.confirm({
                title: 'Archive Flag',
                icon: <WarningOutlined />,
                okText: 'Archive',
                onOk: async () => {
                    if (formRef.current) {
                        await formRef.current.validateFields()
                        return handleArchiveFlag(flag)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>Are you sure you want to archive this flag? The flag will no longer be available for use.</p>

                        <p>
                            Please type the Flag KEY, <b>{flag.getKey()}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule, value) => {
                                        if (!value) {
                                            return Promise.reject('Flag KEY is required.')
                                        } else if (value?.trim() !== flag.getKey()) {
                                            return Promise.reject('Flag KEY does not match.')
                                        } else {
                                            return Promise.resolve()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'disable') {
            Modal.confirm({
                title: 'Disable Flag',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    if (formRef.current) {
                        await formRef.current.validateFields()
                        return handleFlagActiveStatus(flag, false)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>Are you sure you want to disable this flag?</p>

                        <p>
                            Please type the Flag KEY, <b>{flag.getKey()}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule, value, callback) => {
                                        if (!value) {
                                            return Promise.reject('Flag name is required.')
                                        } else if (value?.trim() !== flag.getKey()) {
                                            return Promise.reject('Flag name does not match.')
                                        } else {
                                            return Promise.resolve()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else {
            Modal.confirm({
                title: 'Enable Flag',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => handleFlagActiveStatus(flag, true),
                content: (
                    <p>
                        Are you sure you want to enable this flag? The flag effect will immediately be active on all
                        domains which have this flag.
                    </p>
                ),
            } as any)
        }
    }

    const handleOpenFlagDrawer = async (flag?: FlagModel) => {
        const flagToEdit = flag ?? new FlagModel()
        return setFlagsEditor({
            drawerKey: randomstring.generate(),
            flagToEdit,
            showFlagEditor: true,
        })
    }

    const handleCloseFlagDrawer = async () => {
        return setFlagsEditor({
            showFlagEditor: false,
            flagToEdit: new FlagModel(),
        })
    }

    const onFlagUpdated = () => {
        handleCloseFlagDrawer()
        getFlags()
    }

    function render() {
        return (
            <div className={classnames('flags-list')}>
                <DataTableContext.Provider
                    value={{
                        loading: flags.loading,
                        currentDataSource: flagsDataSource,
                        resolveDataSource: noop,
                    }}
                >
                    <DataTableView
                        actions={[
                            <Button key="new-flag" onClick={handleOpenFlagDrawer} size="small" type="primary">
                                New Flag
                            </Button>,
                        ]}
                        filters={getFilters}
                        onFiltersChange={(filter) => filterFlags(filter['entity-filter'])}
                        onSearchChange={searchFlags}
                        searchPlaceholder="Search flag name ..."
                    >
                        <Table
                            bordered={false}
                            dataSource={flagsDataSource}
                            loading={
                                flags.loading && {
                                    indicator: <Loading3QuartersOutlined spin={true} />,
                                }
                            }
                            locale={{
                                emptyText: <AntdTableEmptyPlaceholder text="No Flags" icon="flags" />,
                            }}
                            pagination={false}
                            footer={(currentTableData) => (
                                <SwSimpleLinkPagination
                                    currentTableData={currentTableData}
                                    links={flags.data.meta?.links}
                                    onPrev={(_ev, _link, config) => {
                                        setPagination({
                                            ...pagination,
                                            current: config.current!,
                                        })
                                    }}
                                    onNext={(_ev, _link, config) => {
                                        setPagination({
                                            ...pagination,
                                            current: config.current!,
                                        })
                                    }}
                                />
                            )}
                            rowKey={(flag) => flag.getKey()}
                            size="small"
                        >
                            <Table.Column
                                align="left"
                                className="flag-details"
                                title="Flag"
                                render={renderFlagDetails}
                            />
                            <Table.Column
                                align="center"
                                className="dev-only"
                                key="isDevOnly"
                                title="Dev Only"
                                render={(active) => renderIsActive(active, 'dev')}
                            />
                            <Table.Column
                                align="right"
                                className="actions"
                                key="actions"
                                title="Actions"
                                render={renderActions}
                            />
                        </Table>
                    </DataTableView>
                </DataTableContext.Provider>

                <FlagsEditor
                    key={flagsEditor.drawerKey}
                    flag={flagsEditor.flagToEdit}
                    mode={flagsEditor.flagToEdit?.getKey?.() ? 'edit' : 'create'}
                    onClose={handleCloseFlagDrawer}
                    onSubmit={onFlagUpdated}
                    visible={flagsEditor.showFlagEditor}
                />
            </div>
        )
    }

    return render()
}
